/**
 * SignLanguageInterpreter - Comprehensive sign language interpretation system
 * Features:
 * - Live interpreter video feed
 * - AI-powered avatar with customizable appearance
 * - Multiple sign language types (ASL, BSL, Auslan, South African, Zimbabwe)
 * - Audience-based customization
 * - Caption/transcript sync
 * - Position and size controls
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Hand, Volume2, VolumeX, Maximize2, Minimize2, Settings, 
  Move, Users, Globe, Eye, EyeOff, Monitor, Smartphone,
  Palette, Type, User, Play, Pause, RefreshCw, Mic, MicOff
} from 'lucide-react';
import { toast } from 'sonner';

// Sign Language Types with regional info - 25+ languages supported
const SIGN_LANGUAGES = [
  // Africa
  { code: 'sasl', name: 'SASL', fullName: 'South African Sign Language', region: 'South Africa', flag: '🇿🇦', family: 'BANZSL' },
  { code: 'zsl', name: 'ZSL', fullName: 'Zimbabwe Sign Language', region: 'Zimbabwe', flag: '🇿🇼', family: 'African' },
  { code: 'ksl', name: 'KSL', fullName: 'Kenyan Sign Language', region: 'Kenya', flag: '🇰🇪', family: 'African' },
  { code: 'ugsl', name: 'UGSL', fullName: 'Ugandan Sign Language', region: 'Uganda', flag: '🇺🇬', family: 'African' },
  { code: 'tsl', name: 'TSL', fullName: 'Tanzanian Sign Language', region: 'Tanzania', flag: '🇹🇿', family: 'African' },
  { code: 'gsl', name: 'GSL', fullName: 'Ghanaian Sign Language', region: 'Ghana', flag: '🇬🇭', family: 'African' },
  { code: 'nsl', name: 'NSL', fullName: 'Nigerian Sign Language', region: 'Nigeria', flag: '🇳🇬', family: 'African' },
  { code: 'esl', name: 'ESL', fullName: 'Ethiopian Sign Language', region: 'Ethiopia', flag: '🇪🇹', family: 'African' },
  // Americas
  { code: 'asl', name: 'ASL', fullName: 'American Sign Language', region: 'USA/Canada', flag: '🇺🇸', family: 'French' },
  { code: 'lsm', name: 'LSM', fullName: 'Mexican Sign Language', region: 'Mexico', flag: '🇲🇽', family: 'French' },
  { code: 'libras', name: 'Libras', fullName: 'Brazilian Sign Language', region: 'Brazil', flag: '🇧🇷', family: 'French' },
  { code: 'lsa', name: 'LSA', fullName: 'Argentine Sign Language', region: 'Argentina', flag: '🇦🇷', family: 'Spanish' },
  // Europe
  { code: 'bsl', name: 'BSL', fullName: 'British Sign Language', region: 'UK', flag: '🇬🇧', family: 'BANZSL' },
  { code: 'lsf', name: 'LSF', fullName: 'French Sign Language', region: 'France', flag: '🇫🇷', family: 'French' },
  { code: 'dgs', name: 'DGS', fullName: 'German Sign Language', region: 'Germany', flag: '🇩🇪', family: 'German' },
  { code: 'lis', name: 'LIS', fullName: 'Italian Sign Language', region: 'Italy', flag: '🇮🇹', family: 'French' },
  { code: 'lse', name: 'LSE', fullName: 'Spanish Sign Language', region: 'Spain', flag: '🇪🇸', family: 'Spanish' },
  { code: 'pjm', name: 'PJM', fullName: 'Polish Sign Language', region: 'Poland', flag: '🇵🇱', family: 'German' },
  { code: 'rsl', name: 'RSL', fullName: 'Russian Sign Language', region: 'Russia', flag: '🇷🇺', family: 'Russian' },
  // Asia-Pacific
  { code: 'auslan', name: 'Auslan', fullName: 'Australian Sign Language', region: 'Australia', flag: '🇦🇺', family: 'BANZSL' },
  { code: 'nzsl', name: 'NZSL', fullName: 'New Zealand Sign Language', region: 'New Zealand', flag: '🇳🇿', family: 'BANZSL' },
  { code: 'jsl', name: 'JSL', fullName: 'Japanese Sign Language', region: 'Japan', flag: '🇯🇵', family: 'Japanese' },
  { code: 'csl', name: 'CSL', fullName: 'Chinese Sign Language', region: 'China', flag: '🇨🇳', family: 'Chinese' },
  { code: 'isl_india', name: 'ISL', fullName: 'Indian Sign Language', region: 'India', flag: '🇮🇳', family: 'Indo-Pakistani' },
  { code: 'hksl', name: 'HKSL', fullName: 'Hong Kong Sign Language', region: 'Hong Kong', flag: '🇭🇰', family: 'Chinese' },
  // International
  { code: 'is', name: 'IS', fullName: 'International Sign', region: 'International', flag: '🌍', family: 'International' },
];

// Audience presets
const AUDIENCE_PRESETS = [
  { id: 'general', name: 'General Audience', description: 'Standard settings for mixed audiences', settings: { avatarSize: 'medium', position: 'bottom-right', showCaptions: true, captionSize: 'medium' } },
  { id: 'deaf-primary', name: 'Deaf/HoH Primary', description: 'Larger interpreter, prominent placement', settings: { avatarSize: 'large', position: 'right-main', showCaptions: true, captionSize: 'large' } },
  { id: 'conference', name: 'Conference/Lecture', description: 'Professional layout for formal events', settings: { avatarSize: 'medium', position: 'bottom-left', showCaptions: true, captionSize: 'medium' } },
  { id: 'workshop', name: 'Workshop/Interactive', description: 'Flexible for hands-on sessions', settings: { avatarSize: 'small', position: 'floating', showCaptions: false, captionSize: 'small' } },
  { id: 'virtual', name: 'Virtual Event', description: 'Optimized for online viewing', settings: { avatarSize: 'large', position: 'side-by-side', showCaptions: true, captionSize: 'large' } },
];

// Avatar appearance options
const AVATAR_STYLES = [
  { id: 'realistic', name: 'Realistic', description: 'Human-like appearance' },
  { id: 'animated', name: 'Animated', description: 'Cartoon-style avatar' },
  { id: 'silhouette', name: 'Silhouette', description: 'Simple hand shapes' },
  { id: 'professional', name: 'Professional', description: 'Business attire avatar' },
];

const SKIN_TONES = [
  { id: 'light', name: 'Light', color: '#FFDCB5' },
  { id: 'medium-light', name: 'Medium Light', color: '#E5B887' },
  { id: 'medium', name: 'Medium', color: '#C68642' },
  { id: 'medium-dark', name: 'Medium Dark', color: '#8D5524' },
  { id: 'dark', name: 'Dark', color: '#5C3317' },
];

const POSITIONS = [
  { id: 'bottom-right', name: 'Bottom Right', icon: '↘' },
  { id: 'bottom-left', name: 'Bottom Left', icon: '↙' },
  { id: 'top-right', name: 'Top Right', icon: '↗' },
  { id: 'top-left', name: 'Top Left', icon: '↖' },
  { id: 'right-main', name: 'Right Side (Main)', icon: '→' },
  { id: 'left-main', name: 'Left Side (Main)', icon: '←' },
  { id: 'side-by-side', name: 'Side by Side', icon: '⇔' },
  { id: 'floating', name: 'Floating (Draggable)', icon: '✦' },
];

// Avatar Animation Component
const SignLanguageAvatar = ({ 
  text = '', 
  isActive = false, 
  language = 'asl',
  style = 'realistic',
  skinTone = 'medium',
  size = 'medium',
  showHandTracking = true
}) => {
  const [currentSign, setCurrentSign] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationRef = useRef(null);

  // Simulate sign language animation
  useEffect(() => {
    if (isActive && text) {
      const words = text.split(' ');
      let index = 0;
      
      animationRef.current = setInterval(() => {
        setCurrentSign(index);
        setIsAnimating(true);
        index = (index + 1) % words.length;
        
        setTimeout(() => setIsAnimating(false), 400);
      }, 800);
      
      return () => {
        if (animationRef.current) clearInterval(animationRef.current);
      };
    }
  }, [isActive, text]);

  const sizeClasses = {
    small: 'w-32 h-28',
    medium: 'w-48 h-40',
    large: 'w-64 h-56',
    xlarge: 'w-80 h-72'
  };

  const skinToneColor = SKIN_TONES.find(t => t.id === skinTone)?.color || SKIN_TONES[2].color;

  return (
    <div 
      className={`${sizeClasses[size]} relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg overflow-hidden border-2 border-blue-500/50 shadow-xl`}
      data-testid="sign-language-avatar"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '16px 16px'
        }} />
      </div>

      {/* Avatar body */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Head */}
          <div 
            className={`w-12 h-14 rounded-full mx-auto transition-transform duration-300 ${isAnimating ? 'scale-105' : ''}`}
            style={{ backgroundColor: skinToneColor }}
          >
            {/* Face features */}
            <div className="absolute top-4 left-2 w-2 h-2 bg-slate-800 rounded-full" />
            <div className="absolute top-4 right-2 w-2 h-2 bg-slate-800 rounded-full" />
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-3 h-1 bg-slate-700/50 rounded-full" />
          </div>
          
          {/* Body */}
          <div className="w-16 h-10 bg-blue-600 rounded-t-lg mx-auto -mt-1" />
          
          {/* Hands - animated */}
          <div className={`absolute -left-6 top-8 transition-all duration-500 ${isAnimating ? 'translate-y-[-8px] rotate-12' : 'rotate-0'}`}>
            <div 
              className="w-8 h-10 rounded-lg shadow-md"
              style={{ backgroundColor: skinToneColor }}
            >
              {showHandTracking && (
                <div className="absolute inset-0 flex flex-col justify-end p-1">
                  <div className="flex gap-0.5 justify-center">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div 
                        key={i} 
                        className={`w-1 bg-white/30 rounded-full transition-all duration-300 ${isAnimating ? 'h-4' : 'h-2'}`}
                        style={{ animationDelay: `${i * 50}ms` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className={`absolute -right-6 top-8 transition-all duration-500 ${isAnimating ? 'translate-y-[-12px] rotate-[-15deg]' : 'rotate-0'}`}>
            <div 
              className="w-8 h-10 rounded-lg shadow-md"
              style={{ backgroundColor: skinToneColor }}
            >
              {showHandTracking && (
                <div className="absolute inset-0 flex flex-col justify-end p-1">
                  <div className="flex gap-0.5 justify-center">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div 
                        key={i} 
                        className={`w-1 bg-white/30 rounded-full transition-all duration-300 ${isAnimating ? 'h-3' : 'h-2'}`}
                        style={{ animationDelay: `${i * 50}ms` }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="absolute top-2 left-2 flex items-center gap-1">
        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
        <span className="text-[10px] text-white/70">{isActive ? 'LIVE' : 'PAUSED'}</span>
      </div>

      {/* Language badge */}
      <div className="absolute top-2 right-2">
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5">
          {SIGN_LANGUAGES.find(l => l.code === language)?.name || language.toUpperCase()}
        </Badge>
      </div>

      {/* Current word indicator */}
      {text && isActive && (
        <div className="absolute bottom-2 left-2 right-2">
          <div className="bg-black/60 rounded px-2 py-1 text-center">
            <span className="text-white text-xs font-medium truncate block">
              {text.split(' ')[currentSign] || '...'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Sign Language Interpreter Panel
const SignLanguageInterpreter = ({ 
  eventId, 
  sessionId,
  streamUrl,
  initialSettings = {},
  onSettingsChange,
  mode = 'viewer' // 'viewer' | 'admin'
}) => {
  const [settings, setSettings] = useState({
    enabled: true,
    type: 'ai_avatar', // 'live' | 'ai_avatar'
    language: 'asl',
    position: 'bottom-right',
    size: 'medium',
    showCaptions: true,
    captionSize: 'medium',
    avatarStyle: 'realistic',
    skinTone: 'medium',
    liveStreamUrl: '',
    audiencePreset: 'general',
    opacity: 100,
    showHandTracking: true,
    voiceToSign: false,
    ...initialSettings
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [currentCaption, setCurrentCaption] = useState('');
  const [captions, setCaptions] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');

  // Update parent when settings change
  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange(settings);
    }
  }, [settings, onSettingsChange]);

  // Apply audience preset
  const applyPreset = (presetId) => {
    const preset = AUDIENCE_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setSettings(prev => ({
        ...prev,
        audiencePreset: presetId,
        size: preset.settings.avatarSize,
        position: preset.settings.position,
        showCaptions: preset.settings.showCaptions,
        captionSize: preset.settings.captionSize
      }));
      toast.success(`Applied "${preset.name}" preset`);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Position styles for the overlay
  const getPositionStyle = () => {
    const positionMap = {
      'bottom-right': { bottom: 16, right: 16 },
      'bottom-left': { bottom: 16, left: 16 },
      'top-right': { top: 16, right: 16 },
      'top-left': { top: 16, left: 16 },
      'right-main': { top: '50%', right: 16, transform: 'translateY(-50%)' },
      'left-main': { top: '50%', left: 16, transform: 'translateY(-50%)' },
    };
    return positionMap[settings.position] || positionMap['bottom-right'];
  };

  if (!settings.enabled) {
    return null;
  }

  // Admin configuration panel
  if (mode === 'admin') {
    return (
      <Card className="border-blue-200" data-testid="sign-language-admin">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Hand className="h-5 w-5 text-blue-600" />
            Sign Language Interpreter Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
              <TabsTrigger value="settings">General</TabsTrigger>
              <TabsTrigger value="appearance">Appearance</TabsTrigger>
              <TabsTrigger value="audience">Audience</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
            </TabsList>

            {/* General Settings */}
            <TabsContent value="settings" className="space-y-4 mt-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Enable Sign Language</Label>
                  <p className="text-xs text-slate-500">Show interpreter during sessions</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(v) => updateSetting('enabled', v)}
                />
              </div>

              <div className="space-y-2">
                <Label>Interpreter Type</Label>
                <Select value={settings.type} onValueChange={(v) => updateSetting('type', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ai_avatar">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>AI Avatar (Automated)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="live">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        <span>Live Interpreter (Video Feed)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.type === 'live' && (
                <div className="space-y-2">
                  <Label>Live Stream URL</Label>
                  <Input
                    value={settings.liveStreamUrl}
                    onChange={(e) => updateSetting('liveStreamUrl', e.target.value)}
                    placeholder="https://stream.example.com/interpreter"
                  />
                  <p className="text-xs text-slate-500">Enter the video stream URL for the live interpreter</p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Sign Language Type</Label>
                <Select value={settings.language} onValueChange={(v) => updateSetting('language', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGN_LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <div className="flex items-center gap-2">
                          <span>{lang.flag}</span>
                          <span>{lang.name}</span>
                          <span className="text-xs text-slate-500">({lang.region})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Position</Label>
                <Select value={settings.position} onValueChange={(v) => updateSetting('position', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSITIONS.map(pos => (
                      <SelectItem key={pos.id} value={pos.id}>
                        <div className="flex items-center gap-2">
                          <span className="w-5 text-center">{pos.icon}</span>
                          <span>{pos.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Size</Label>
                <Select value={settings.size} onValueChange={(v) => updateSetting('size', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                    <SelectItem value="xlarge">Extra Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Opacity: {settings.opacity}%</Label>
                <Slider
                  value={[settings.opacity]}
                  onValueChange={([v]) => updateSetting('opacity', v)}
                  min={50}
                  max={100}
                  step={5}
                />
              </div>
            </TabsContent>

            {/* Appearance Settings */}
            <TabsContent value="appearance" className="space-y-4 mt-4">
              {settings.type === 'ai_avatar' && (
                <>
                  <div className="space-y-2">
                    <Label>Avatar Style</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {AVATAR_STYLES.map(style => (
                        <Button
                          key={style.id}
                          variant={settings.avatarStyle === style.id ? 'default' : 'outline'}
                          className="h-auto py-3 flex flex-col items-start"
                          onClick={() => updateSetting('avatarStyle', style.id)}
                        >
                          <span className="font-medium">{style.name}</span>
                          <span className="text-xs opacity-70">{style.description}</span>
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Skin Tone</Label>
                    <div className="flex gap-2">
                      {SKIN_TONES.map(tone => (
                        <button
                          key={tone.id}
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            settings.skinTone === tone.id ? 'border-blue-600 scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: tone.color }}
                          onClick={() => updateSetting('skinTone', tone.id)}
                          title={tone.name}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="font-medium">Show Hand Tracking</Label>
                      <p className="text-xs text-slate-500">Visual finger indicators</p>
                    </div>
                    <Switch
                      checked={settings.showHandTracking}
                      onCheckedChange={(v) => updateSetting('showHandTracking', v)}
                    />
                  </div>
                </>
              )}

              <Separator />

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <Label className="font-medium">Show Captions</Label>
                  <p className="text-xs text-slate-500">Display text along with signs</p>
                </div>
                <Switch
                  checked={settings.showCaptions}
                  onCheckedChange={(v) => updateSetting('showCaptions', v)}
                />
              </div>

              {settings.showCaptions && (
                <div className="space-y-2">
                  <Label>Caption Size</Label>
                  <Select value={settings.captionSize} onValueChange={(v) => updateSetting('captionSize', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </TabsContent>

            {/* Audience Presets */}
            <TabsContent value="audience" className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Select a preset optimized for your audience type, or customize individually.
              </p>
              
              <div className="grid gap-3">
                {AUDIENCE_PRESETS.map(preset => (
                  <div
                    key={preset.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      settings.audiencePreset === preset.id 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => applyPreset(preset.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {preset.name}
                        </h4>
                        <p className="text-sm text-slate-500">{preset.description}</p>
                      </div>
                      {settings.audiencePreset === preset.id && (
                        <Badge>Active</Badge>
                      )}
                    </div>
                    <div className="mt-2 flex gap-2 text-xs text-slate-500">
                      <span>Size: {preset.settings.avatarSize}</span>
                      <span>•</span>
                      <span>Position: {preset.settings.position}</span>
                      <span>•</span>
                      <span>Captions: {preset.settings.showCaptions ? 'On' : 'Off'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Preview */}
            <TabsContent value="preview" className="mt-4">
              <div className="bg-slate-100 rounded-lg p-6 flex items-center justify-center min-h-[300px]">
                <div className="text-center">
                  <SignLanguageAvatar
                    text="This is a preview of the sign language interpreter"
                    isActive={settings.enabled}
                    language={settings.language}
                    style={settings.avatarStyle}
                    skinTone={settings.skinTone}
                    size={settings.size}
                    showHandTracking={settings.showHandTracking}
                  />
                  <p className="text-sm text-slate-500 mt-4">
                    Live preview of interpreter settings
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    );
  }

  // Viewer mode - overlay component
  return (
    <div 
      className="fixed z-50 transition-all duration-300"
      style={{ 
        ...getPositionStyle(),
        opacity: settings.opacity / 100
      }}
      data-testid="sign-language-overlay"
    >
      <div className="relative group">
        {settings.type === 'ai_avatar' ? (
          <SignLanguageAvatar
            text={currentCaption}
            isActive={settings.enabled}
            language={settings.language}
            style={settings.avatarStyle}
            skinTone={settings.skinTone}
            size={settings.size}
            showHandTracking={settings.showHandTracking}
          />
        ) : settings.type === 'live' && settings.liveStreamUrl ? (
          <div className={`bg-black rounded-lg overflow-hidden border-2 border-blue-500/50 ${
            settings.size === 'small' ? 'w-32 h-28' :
            settings.size === 'medium' ? 'w-48 h-40' :
            settings.size === 'large' ? 'w-64 h-56' : 'w-80 h-72'
          }`}>
            <iframe
              src={settings.liveStreamUrl}
              className="w-full h-full"
              allow="camera; microphone"
              title="Live Sign Language Interpreter"
            />
          </div>
        ) : null}

        {/* Quick controls - show on hover */}
        <div className="absolute -top-8 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-1">
          <Button variant="secondary" size="icon" className="h-6 w-6" title="Toggle visibility">
            {settings.enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
          </Button>
          <Button 
            variant="secondary" 
            size="icon" 
            className="h-6 w-6"
            onClick={() => setIsExpanded(!isExpanded)}
            title="Expand/Collapse"
          >
            {isExpanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
          </Button>
        </div>

        {/* Captions */}
        {settings.showCaptions && currentCaption && (
          <div className={`absolute -bottom-8 left-0 right-0 bg-black/80 rounded px-2 py-1 text-center ${
            settings.captionSize === 'small' ? 'text-xs' :
            settings.captionSize === 'large' ? 'text-base' : 'text-sm'
          }`}>
            <span className="text-white">{currentCaption}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignLanguageInterpreter;
export { SignLanguageAvatar, SIGN_LANGUAGES, AUDIENCE_PRESETS };
